import React from 'react';
import clsx from 'clsx';

/* ─── Size map ─────────────────────────────────────────────────────── */

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeMap: Record<AvatarSize, { box: number; font: string; indicator: number }> = {
  xs: { box: 24, font: '0.625rem', indicator: 7 },
  sm: { box: 32, font: '0.75rem',  indicator: 8 },
  md: { box: 40, font: '0.875rem', indicator: 10 },
  lg: { box: 48, font: '1rem',     indicator: 12 },
  xl: { box: 64, font: '1.25rem',  indicator: 14 },
};

/* ─── Stable gradient palette (seeded by name/id) ─────────────────── */

const GRADIENTS: Array<[string, string]> = [
  ['#00afaf', '#006969'],
  ['#6d28d9', '#8b5cf6'],
  ['#dc2626', '#991b1b'],
  ['#f59e0b', '#d97706'],
  ['#0284c7', '#1e40af'],
  ['#15803d', '#14532d'],
  ['#be185d', '#9d174d'],
  ['#0f766e', '#134e4a'],
];

function hashSeed(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return (parts[0][0] ?? '').toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

/* ─── Component ────────────────────────────────────────────────────── */

export interface AvatarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** Image URL (profile photo). Falls back to initials / icon on error or if absent. */
  src?: string | null;
  /** Display name — used for initials and as the gradient seed. */
  name?: string | null;
  /** Size preset */
  size?: AvatarSize;
  /** Show online status dot */
  online?: boolean;
  /** Optional click handler — renders avatar as a button. */
  onClick?: () => void;
  /** Optional explicit seed (e.g. user id) so colour stays stable even if the name changes. */
  seed?: string | null;
  /** Override the background when you want a flat colour (e.g. nav, notifications). */
  background?: string;
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  name = '',
  size = 'md',
  online,
  onClick,
  seed,
  background,
  className,
  style,
  ...props
}) => {
  const [imgError, setImgError] = React.useState(false);
  const s = sizeMap[size];
  const initials = getInitials(name ?? '');
  const safeSeed = (seed ?? name ?? 'heka') || 'heka';
  const [from, to] = GRADIENTS[hashSeed(safeSeed) % GRADIENTS.length];

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: s.box,
    height: s.box,
    minWidth: s.box,
    borderRadius: 'var(--radius-full)',
    background: background ?? `linear-gradient(135deg, ${from}, ${to})`,
    color: 'white',
    fontWeight: 700,
    fontSize: s.font,
    fontFamily: 'var(--font-display)',
    overflow: 'hidden',
    flexShrink: 0,
    userSelect: 'none',
    cursor: onClick ? 'pointer' : 'default',
    border: 'none',
    padding: 0,
    lineHeight: 1,
    ...style,
  };

  const imgStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  };

  const indicatorStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: s.indicator,
    height: s.indicator,
    borderRadius: '50%',
    background: 'var(--heka-success)',
    border: '2px solid var(--surface-base)',
  };

  const showImg = !!src && !imgError;
  const iconPx = Math.round(s.box * 0.55);

  const handleKey = (e: React.KeyboardEvent) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
  };

  const roleValue: React.AriaRole = onClick ? 'button' : 'img';

  return (
    <div
      className={clsx('heka-avatar', className)}
      style={containerStyle}
      role={roleValue}
      aria-label={name || 'User avatar'}
      onClick={onClick}
      onKeyDown={handleKey}
      tabIndex={onClick ? 0 : undefined}
      {...props}
    >
      {showImg ? (
        <img src={src!} alt="" style={imgStyle} onError={() => setImgError(true)} decoding="async" loading="lazy" />
      ) : initials ? (
        <span aria-hidden="true">{initials}</span>
      ) : (
        <svg viewBox="0 0 24 24" width={iconPx} height={iconPx} fill="currentColor" aria-hidden="true">
          <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5Z" />
        </svg>
      )}
      {online !== undefined && online && <span style={indicatorStyle} aria-label="Online" />}
    </div>
  );
};

Avatar.displayName = 'Avatar';
export default Avatar;
